import { useLocation, useNavigate } from "react-router-dom";
import { Globe, Check } from "lucide-react";
import { useLanguage, switchLangPath } from "@/i18n/LanguageContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * Language switcher.
 *
 * One component, several presentation variants used across the layout:
 *
 *   `globe`         — Compact icon button (used in the desktop header
 *                     and in the mobile compact header). Opens a small
 *                     dropdown listing both languages with the current
 *                     one marked. This is the primary control.
 *   `footer-pair`   — Inline pair `العربية | English` always showing
 *                     both languages, with the current one subtly
 *                     highlighted. Used in the footer.
 *   `segmented`     — The original two-pill control. Kept for the
 *                     mobile drawer where the header globe isn't
 *                     visually present.
 *   `text`          — Single-link "switch to the other language" text
 *                     button. Kept for backward compatibility — not
 *                     used in the launch homepage but still imported
 *                     by older surfaces.
 *
 * Switching is purely a navigation operation: we mirror the current
 * pathname under the requested language tree using `switchLangPath()`.
 */
export default function LanguageSwitcher({
  variant = "segmented",
  testId = "lang-switcher",
}) {
  const { lang } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const goTo = (newLang) => {
    if (newLang === lang) return;
    const target =
      switchLangPath(location.pathname, newLang) +
      location.search +
      location.hash;
    navigate(target);
  };

  // ------------------------------------------------------------------ //
  // Globe icon + dropdown (header).
  // ------------------------------------------------------------------ //
  if (variant === "globe") {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="
              inline-flex items-center justify-center w-10 h-10 rounded-full
              border border-[color:var(--sas-line)]
              text-[color:var(--sas-ink-2)]
              hover:text-[color:var(--sas-accent)] hover:border-[color:var(--sas-accent)]
              transition-colors
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
              focus-visible:ring-[color:var(--sas-accent)]
              sas-glass
            "
            data-testid={testId}
            aria-label={lang === "ar" ? "تغيير اللغة" : "Change language"}
          >
            <Globe size={17} aria-hidden />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={8}
          className="min-w-[180px] sas-glass"
        >
          <DropdownMenuLabel className="text-[11px] tracking-wide uppercase text-[color:var(--sas-ink-2)]">
            {lang === "ar" ? "اللغة" : "Language"}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => goTo("ar")}
            data-testid={`${testId}-ar`}
            lang="ar"
            dir="rtl"
            className="cursor-pointer flex items-center justify-between gap-2 text-[14px]"
          >
            <span>العربية</span>
            {lang === "ar" && (
              <Check
                size={15}
                aria-hidden
                className="text-[color:var(--sas-accent)]"
              />
            )}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => goTo("en")}
            data-testid={`${testId}-en`}
            lang="en"
            dir="ltr"
            className="cursor-pointer flex items-center justify-between gap-2 text-[14px]"
          >
            <span>English</span>
            {lang === "en" && (
              <Check
                size={15}
                aria-hidden
                className="text-[color:var(--sas-accent)]"
              />
            )}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // ------------------------------------------------------------------ //
  // Footer pair: «العربية | English» — always show both, highlight active.
  // ------------------------------------------------------------------ //
  if (variant === "footer-pair") {
    const active =
      "text-[color:var(--sas-ink)] font-medium underline underline-offset-4 decoration-[color:var(--sas-accent)] decoration-[1.5px]";
    const inactive =
      "text-[color:var(--sas-ink-2)] hover:text-[color:var(--sas-ink)] transition-colors";
    return (
      <span
        className="inline-flex items-center gap-3 text-[12px]"
        data-testid={testId}
      >
        <button
          type="button"
          onClick={() => goTo("ar")}
          aria-current={lang === "ar" ? "true" : undefined}
          aria-pressed={lang === "ar"}
          lang="ar"
          dir="rtl"
          className={lang === "ar" ? active : inactive}
          data-testid={`${testId}-ar`}
        >
          العربية
        </button>
        <span className="opacity-30 select-none" aria-hidden>
          |
        </span>
        <button
          type="button"
          onClick={() => goTo("en")}
          aria-current={lang === "en" ? "true" : undefined}
          aria-pressed={lang === "en"}
          lang="en"
          dir="ltr"
          className={lang === "en" ? active : inactive}
          data-testid={`${testId}-en`}
        >
          English
        </button>
      </span>
    );
  }

  // ------------------------------------------------------------------ //
  // Plain text "switch to the other" (legacy).
  // ------------------------------------------------------------------ //
  if (variant === "text") {
    const targetLang = lang === "ar" ? "en" : "ar";
    const targetLabel = targetLang === "en" ? "English" : "العربية";
    return (
      <button
        type="button"
        onClick={() => goTo(targetLang)}
        className="text-xs text-[color:var(--sas-ink-2)] hover:text-[color:var(--sas-accent)] transition-colors"
        data-testid={testId}
        aria-label={lang === "ar" ? "Switch to English" : "التبديل إلى العربية"}
        lang={targetLang}
        dir={targetLang === "ar" ? "rtl" : "ltr"}
      >
        {targetLabel}
      </button>
    );
  }

  // ------------------------------------------------------------------ //
  // Segmented pill — fallback / drawer.
  // ------------------------------------------------------------------ //
  const btnBase =
    "px-2.5 h-7 inline-flex items-center justify-center text-[11px] font-semibold tracking-wide rounded-full transition-all leading-none";
  return (
    <div
      className="inline-flex items-center p-0.5 rounded-full border border-[color:var(--sas-line)] bg-white"
      data-testid={testId}
      role="group"
      aria-label={lang === "ar" ? "تبديل اللغة" : "Language switcher"}
    >
      <button
        type="button"
        onClick={() => goTo("ar")}
        aria-pressed={lang === "ar"}
        lang="ar"
        dir="rtl"
        className={`${btnBase} ${
          lang === "ar"
            ? "bg-[color:var(--sas-ink)] text-white"
            : "text-[color:var(--sas-ink-2)] hover:text-[color:var(--sas-ink)]"
        }`}
      >
        عربي
      </button>
      <button
        type="button"
        onClick={() => goTo("en")}
        aria-pressed={lang === "en"}
        lang="en"
        dir="ltr"
        className={`${btnBase} ${
          lang === "en"
            ? "bg-[color:var(--sas-ink)] text-white"
            : "text-[color:var(--sas-ink-2)] hover:text-[color:var(--sas-ink)]"
        }`}
      >
        EN
      </button>
    </div>
  );
}
